import { FC, useMemo } from 'react';
import { Route } from 'react-router-dom';

import useCasinoRoutes from '~/components/router/use-casino-routes';
import { getRelativePaths } from '~/utils/pathname';

import CasinoSeoHeader from '~/components/casino-shared/casino-seo-header';
import CasinoSubNavigationSeoHeader from '~/components/casino-shared/subnavigation/casino-subnavigation-seo-header';
import CasinoProviderSeoHeader from '~/components/casino-shared/provider/casino-provider-seo-header';
import CasinoSubProviderSeoHeader from '~/components/casino-shared/subprovider/casino-subprovider-seo-header';
import { TracedRoutes } from '@comeon/error-reporter';

/**
 * Sets general page title and description.
 *
 * Pages that need more specific or dynamic title and description may override whatever
 * is set here using the default override behavior provided by react-helmet.
 */
const CasinoSeoHeaderContainer: FC = () => {
	const {
		ROUTE_CASINO,
		ROUTE_CASINO_ALL_GAMES,
		ROUTE_CASINO_EXPLORE,
		ROUTE_CASINO_SUBMENU,
		ROUTE_CASINO_MY_GAMES,
		ROUTE_CASINO_PROVIDER,
		ROUTE_CASINO_SUBPROVIDER
	} = useCasinoRoutes();

	const paths = useMemo(() => {
		const CHILD_ROUTES = [
			ROUTE_CASINO,
			ROUTE_CASINO_ALL_GAMES,
			ROUTE_CASINO_EXPLORE,
			ROUTE_CASINO_SUBMENU,
			ROUTE_CASINO_MY_GAMES,
			ROUTE_CASINO_PROVIDER,
			ROUTE_CASINO_SUBPROVIDER
		];
		return getRelativePaths(CHILD_ROUTES, ROUTE_CASINO);
	}, [
		ROUTE_CASINO,
		ROUTE_CASINO_ALL_GAMES,
		ROUTE_CASINO_EXPLORE,
		ROUTE_CASINO_MY_GAMES,
		ROUTE_CASINO_PROVIDER,
		ROUTE_CASINO_SUBMENU,
		ROUTE_CASINO_SUBPROVIDER
	]);

	return (
		<TracedRoutes key="casino-routes">
			<Route
				path={paths[ROUTE_CASINO_EXPLORE]}
				element={
					<CasinoSeoHeader
						titleTextKey="CASINO_EXPLORE_SITE_TITLE"
						descriptionTextKey="CASINO_EXPLORE_META_DESCRIPTION"
						keywordsTextKey="CASINO_EXPLORE_META_KEYWORDS"
					/>
				}
			/>
			<Route
				path={paths[ROUTE_CASINO_ALL_GAMES]}
				element={
					<CasinoSeoHeader
						titleTextKey="CASINO_ALL_GAMES_SITE_TITLE"
						descriptionTextKey="CASINO_ALL_GAMES_META_DESCRIPTION"
						keywordsTextKey="CASINO_ALL_GAMES_META_KEYWORDS"
					/>
				}
			/>
			<Route
				path={`${paths[ROUTE_CASINO_ALL_GAMES]}/:filter`}
				element={
					<CasinoSeoHeader
						titleTextKey="CASINO_ALL_GAMES_SITE_TITLE"
						descriptionTextKey="CASINO_ALL_GAMES_META_DESCRIPTION"
						keywordsTextKey="CASINO_ALL_GAMES_META_KEYWORDS"
					/>
				}
			/>
			<Route
				path={`${paths[ROUTE_CASINO_SUBMENU]}/:submenu`}
				element={<CasinoSubNavigationSeoHeader titleFallBackTextkey="CASINO_EXPLORE_SITE_TITLE" />}
			/>
			<Route
				path={`${paths[ROUTE_CASINO_PROVIDER]}/:providerName`}
				element={<CasinoProviderSeoHeader titleFallBackTextkey="CASINO_EXPLORE_SITE_TITLE" />}
			/>
			<Route
				path={`${paths[ROUTE_CASINO_SUBPROVIDER]}/:subproviderCode`}
				element={<CasinoSubProviderSeoHeader titleFallBackTextkey="CASINO_EXPLORE_SITE_TITLE" />}
			/>

			<Route
				path={`${paths[ROUTE_CASINO_MY_GAMES]}/:filter`}
				element={
					<CasinoSeoHeader
						titleTextKey="CASINO_LOVED_GAMES_SITE_TITLE"
						descriptionTextKey="CASINO_LOVED_GAMES_META_DESCRIPTION"
						keywordsTextKey="CASINO_LOVED_GAMES_META_KEYWORDS"
					/>
				}
			/>
		</TracedRoutes>
	);
};

export default CasinoSeoHeaderContainer;
